import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, Ship, PauseCircle, Plus, Search, ExternalLink,
  QrCode, Trash2, PlayCircle, ChevronUp, ChevronDown, AlertTriangle,
  Calendar, Activity, LayoutGrid, LayoutList, RefreshCw, TrendingUp,
} from 'lucide-react';
import { listCompanies, toggleCompanyStatus, deleteCompany, getGlobalActivity, Company } from '../services/api';
import { fmtDateJst, parseUtc } from '../utils/datetime';
import QRCode from 'qrcode';

const ADMIN_DOMAIN = 'unison-sea.com';
const DL_BASE      = 'https://dl.unison-sea.com';

type SortKey = 'created_at' | 'name' | 'user_count' | 'vessel_count' | 'renewal_date' | 'last_activity';
type ViewMode = 'card' | 'table';
interface QrModal { company: Company; apkQr: string; personalQr: string; adminQr: string; }

function daysUntil(dateStr: string | null): number | null {
  const d = parseUtc(dateStr);
  if (!d) return null;
  const diff = d.getTime() - Date.now();
  return Math.ceil(diff / 86400000);
}

function fmtDate(dateStr: string | null): string {
  return fmtDateJst(dateStr, '—'); // 日本時刻(Asia/Tokyo)で表示
}

function fmtRelative(dateStr: string | null): string {
  const d = parseUtc(dateStr);
  if (!d) return '未利用';
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return '今日';
  if (days === 1) return '昨日';
  if (days < 7)  return `${days}日前`;
  if (days < 30) return `${Math.floor(days / 7)}週間前`;
  if (days < 365) return `${Math.floor(days / 30)}ヶ月前`;
  return `${Math.floor(days / 365)}年以上前`;
}

function RenewalBadge({ date }: { date: string | null }) {
  const days = daysUntil(date);
  if (days === null) return <span style={{ fontSize: 11, color: '#9ca3af' }}>未設定</span>;
  if (days < 0)   return <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', background: '#fef2f2', padding: '2px 6px', borderRadius: 4 }}>期限切れ</span>;
  if (days <= 30) return <span style={{ fontSize: 11, fontWeight: 700, color: '#d97706', background: '#fffbeb', padding: '2px 6px', borderRadius: 4 }}>{days}日後</span>;
  if (days <= 90) return <span style={{ fontSize: 11, fontWeight: 600, color: '#2563eb', background: '#eff6ff', padding: '2px 6px', borderRadius: 4 }}>{fmtDate(date)}</span>;
  return <span style={{ fontSize: 11, color: '#6b7280' }}>{fmtDate(date)}</span>;
}

export default function DashboardPage({ saRole }: { saRole?: string }) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]         = useState('');
  const [modal, setModal]         = useState<QrModal | null>(null);
  const [search, setSearch]       = useState('');
  const [sortKey, setSortKey]     = useState<SortKey>('created_at');
  const [sortAsc, setSortAsc]     = useState(false);
  const [toggling, setToggling]   = useState<string | null>(null);
  const [deleting, setDeleting]   = useState<string | null>(null);
  const [viewMode, setViewMode]   = useState<ViewMode>('card');
  const [monthStamps, setMonthStamps] = useState(0);
  const [prevMonthStamps, setPrevMonthStamps] = useState(0);
  const navigate = useNavigate();
  const isOwner = saRole === 'owner';

  useEffect(() => {
    load();
    // 今月・先月の打刻数
    Promise.all([
      getGlobalActivity(30),
      getGlobalActivity(60),
    ]).then(([cur, prev]) => {
      setMonthStamps(cur.daily.reduce((s, d) => s + d.count, 0));
      const prevTotal = prev.daily.reduce((s, d) => s + d.count, 0);
      setPrevMonthStamps(Math.round((prevTotal - cur.daily.reduce((s, d) => s + d.count, 0))));
    }).catch(() => {});
  }, []);

  function load(silent = false) {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    listCompanies()
      .then(setCompanies)
      .catch(e => setError(e instanceof Error ? e.message : 'エラーが発生しました'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }

  const stats = useMemo(() => {
    const expiringCount = companies.filter(c => {
      const d = daysUntil(c.renewal_date);
      return d !== null && d >= 0 && d <= 30;
    }).length;
    const expiredCount = companies.filter(c => {
      const d = daysUntil(c.renewal_date);
      return d !== null && d < 0;
    }).length;
    return {
      total:    companies.length,
      active:   companies.filter(c => c.is_active).length,
      users:    companies.reduce((s, c) => s + c.user_count, 0),
      vessels:  companies.reduce((s, c) => s + c.vessel_count, 0),
      expiring: expiringCount,
      expired:  expiredCount,
    };
  }, [companies]);

  const expiringCompanies = useMemo(() =>
    companies
      .filter(c => { const d = daysUntil(c.renewal_date); return d !== null && d <= 30; })
      .sort((a, b) => (a.renewal_date ?? '').localeCompare(b.renewal_date ?? '')),
    [companies]
  );

  const filtered = useMemo(() => {
    let list = companies.filter(c =>
      c.name.includes(search) || c.subdomain.includes(search) ||
      (c.sales_contact ?? '').includes(search) || (c.memo ?? '').includes(search)
    );
    list = [...list].sort((a, b) => {
      let v = 0;
      if (sortKey === 'name')            v = a.name.localeCompare(b.name, 'ja');
      else if (sortKey === 'user_count')    v = a.user_count - b.user_count;
      else if (sortKey === 'vessel_count')  v = a.vessel_count - b.vessel_count;
      else if (sortKey === 'renewal_date')  v = (a.renewal_date ?? '9999').localeCompare(b.renewal_date ?? '9999');
      else if (sortKey === 'last_activity') v = (a.last_activity ?? '').localeCompare(b.last_activity ?? '');
      else v = a.created_at.localeCompare(b.created_at);
      return sortAsc ? v : -v;
    });
    return list;
  }, [companies, search, sortKey, sortAsc]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc(p => !p);
    else { setSortKey(key); setSortAsc(key === 'renewal_date'); }
  }

  async function handleDelete(e: React.MouseEvent, c: Company) {
    e.stopPropagation();
    if (!window.confirm(`「${c.name}」を完全に削除しますか？\n\nこの操作は取り消せません。`)) return;
    if (!window.confirm(`再確認：「${c.name}」を削除します。`)) return;
    setDeleting(c.id);
    try { await deleteCompany(c.id); setCompanies(prev => prev.filter(x => x.id !== c.id)); }
    catch (err) { alert(err instanceof Error ? err.message : 'エラーが発生しました'); }
    finally { setDeleting(null); }
  }

  async function handleToggleStatus(e: React.MouseEvent, c: Company) {
    e.stopPropagation();
    if (!window.confirm(`「${c.name}」を${c.is_active ? '停止' : '再開'}しますか？`)) return;
    setToggling(c.id);
    try {
      const updated = await toggleCompanyStatus(c.id);
      setCompanies(prev => prev.map(x => x.id === c.id ? { ...x, is_active: updated.is_active } : x));
    } catch (err) { alert(err instanceof Error ? err.message : 'エラーが発生しました'); }
    finally { setToggling(null); }
  }

  async function openQr(e: React.MouseEvent, c: Company) {
    e.stopPropagation();
    const [apkQr, personalQr, adminQr] = await Promise.all([
      QRCode.toDataURL(`${DL_BASE}/?apk=catms-${c.subdomain}.apk&company=${encodeURIComponent(c.name)}`, { width: 220, margin: 2 }),
      // 船員の個人スマホ（iPhone・Android）はAPKではなくWebアプリ。読み取ったらホーム画面に追加する
      QRCode.toDataURL(`https://${c.subdomain}.crew.${ADMIN_DOMAIN}/?app=personal`, { width: 220, margin: 2 }),
      QRCode.toDataURL(`https://${c.subdomain}.${ADMIN_DOMAIN}`, { width: 220, margin: 2 }),
    ]);
    setModal({ company: c, apkQr, personalQr, adminQr });
  }

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <span style={{ color: '#d1d5db', fontSize: 10, marginLeft: 3 }}>↕</span>;
    return sortAsc
      ? <ChevronUp size={12} style={{ marginLeft: 2 }} />
      : <ChevronDown size={12} style={{ marginLeft: 2 }} />;
  };

  const stampGrowth = prevMonthStamps > 0
    ? Math.round(((monthStamps - prevMonthStamps) / prevMonthStamps) * 100)
    : null;

  return (
    <div>
      {/* KPIカード */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: '契約会社',   value: stats.total,                sub: `稼働中 ${stats.active} 社`,     Icon: Building2 },
          { label: '停止中',     value: stats.total - stats.active, sub: '利用停止',                       Icon: PauseCircle },
          { label: '総船員数',   value: stats.users,                sub: '全社合計',                       Icon: Users },
          { label: '総船舶数',   value: stats.vessels,              sub: '全社合計',                       Icon: Ship },
          {
            label: '今月の打刻',
            value: monthStamps.toLocaleString(),
            sub: stampGrowth !== null ? `先月比 ${stampGrowth >= 0 ? '+' : ''}${stampGrowth}%` : '過去30日',
            Icon: Activity,
            growthSign: stampGrowth,
          },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', borderRadius: 10, padding: '16px 18px', border: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', letterSpacing: '0.02em', textTransform: 'uppercase' }}>{s.label}</span>
              <s.Icon size={14} color="#d1d5db" strokeWidth={1.8} />
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#111827', letterSpacing: '-0.8px', lineHeight: 1 }}>{s.value}</div>
            {'growthSign' in s && s.growthSign !== null && s.growthSign !== undefined ? (
              <div style={{ fontSize: 11, color: s.growthSign >= 0 ? '#16a34a' : '#dc2626', marginTop: 6, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                <TrendingUp size={10} />
                {s.sub}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>{s.sub}</div>
            )}
          </div>
        ))}
      </div>

      {/* 更新期限アラート */}
      {expiringCompanies.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <AlertTriangle size={16} color="#d97706" />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
              契約更新が近い会社 — {expiringCompanies.length}件
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {expiringCompanies.map(c => {
              const days = daysUntil(c.renewal_date)!;
              const isExpired = days < 0;
              const isUrgent = days >= 0 && days <= 14;
              return (
                <div key={c.id} onClick={() => navigate(`/companies/${c.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: isExpired ? '#fef2f2' : isUrgent ? '#fff7ed' : '#fff', border: `1px solid ${isExpired ? '#fecaca' : isUrgent ? '#fed7aa' : '#fde68a'}`, borderRadius: 8, padding: '7px 12px', cursor: 'pointer', flexShrink: 0 }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: isExpired ? '#dc2626' : isUrgent ? '#ea580c' : '#d97706', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#1f2937' }}>{c.name}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: isExpired ? '#dc2626' : isUrgent ? '#ea580c' : '#d97706' }}>
                    {isExpired ? `${Math.abs(days)}日超過` : days === 0 ? '本日期限' : `残${days}日`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ツールバー */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>契約会社</h2>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>
            {search ? `${filtered.length} / ${companies.length} 社` : `${companies.length} 社`}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <Search size={13} color="#9ca3af" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="会社名・担当・メモ検索"
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '7px 12px 7px 30px', fontSize: 12, width: 200, outline: 'none', background: '#fff', color: '#111827' }} />
          </div>
          {/* ビュー切り替え */}
          <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 8, padding: 3, gap: 2 }}>
            {([['card', LayoutGrid], ['table', LayoutList]] as [ViewMode, typeof LayoutGrid][]).map(([mode, Icon]) => (
              <button key={mode} onClick={() => setViewMode(mode)} style={{ padding: '5px 8px', borderRadius: 6, border: 'none', cursor: 'pointer', background: viewMode === mode ? '#fff' : 'transparent', boxShadow: viewMode === mode ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', color: viewMode === mode ? '#1d4ed8' : '#6b7280' }}>
                <Icon size={14} />
              </button>
            ))}
          </div>
          <button onClick={() => load(true)} disabled={refreshing} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
            <RefreshCw size={13} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </button>
          <button onClick={() => navigate('/wizard')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={14} strokeWidth={2.5} /> 新しい会社
          </button>
        </div>
      </div>

      {/* ソートバー */}
      <div style={{ display: 'inline-flex', gap: 2, marginBottom: 14, background: '#fff', borderRadius: 8, padding: '3px', border: '1px solid #e5e7eb' }}>
        {([
          { key: 'created_at'    as SortKey, label: '登録日' },
          { key: 'name'          as SortKey, label: '会社名' },
          { key: 'user_count'    as SortKey, label: '船員数' },
          { key: 'vessel_count'  as SortKey, label: '船舶数' },
          { key: 'renewal_date'  as SortKey, label: '更新日', alert: stats.expiring > 0 },
          { key: 'last_activity' as SortKey, label: '最終利用' },
        ]).map(item => {
          const active = sortKey === item.key;
          return (
            <button key={item.key} onClick={() => toggleSort(item.key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: active ? 600 : 400, padding: '5px 10px', borderRadius: 6, background: active ? '#eff6ff' : 'transparent', color: active ? '#1d4ed8' : '#6b7280' }}>
              {item.label}
              {'alert' in item && item.alert && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />}
              <SortIcon k={item.key} />
            </button>
          );
        })}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 14, color: '#dc2626', fontSize: 13 }}>{error}</div>
      )}

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0', color: '#9ca3af', gap: 8 }}>
          <div style={{ width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#1d4ed8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          読み込み中...
        </div>
      )}

      {!loading && companies.length === 0 && (
        <div style={{ textAlign: 'center', padding: 64, background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
          <div style={{ width: 56, height: 56, background: '#eff6ff', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <Building2 size={24} color="#1d4ed8" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 6 }}>会社が登録されていません</div>
          <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>最初の契約会社を追加しましょう</div>
          <button onClick={() => navigate('/wizard')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={15} /> 会社を追加する
          </button>
        </div>
      )}

      {/* ── カードビュー ── */}
      {!loading && viewMode === 'card' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
          {filtered.map(c => {
            const inactive = !c.is_active;
            const renewDays = daysUntil(c.renewal_date);
            const isRenewAlert = renewDays !== null && renewDays <= 30;
            return (
              <div key={c.id} onClick={() => navigate(`/companies/${c.id}`)}
                style={{ background: '#fff', borderRadius: 12, border: `1px solid ${inactive ? '#fecdd3' : isRenewAlert && renewDays! < 0 ? '#fecdd3' : isRenewAlert ? '#fde68a' : '#e5e7eb'}`, boxShadow: '0 1px 2px rgba(0,0,0,0.04)', cursor: 'pointer', overflow: 'hidden', opacity: inactive ? 0.85 : 1, transition: 'box-shadow 0.15s, transform 0.15s' }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 6px 20px rgba(0,0,0,0.1)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.boxShadow = '0 1px 2px rgba(0,0,0,0.04)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; }}>

                {/* カード上部 */}
                <div style={{ padding: '15px 16px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.subdomain}.unison-sea.com</div>
                    </div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 99, flexShrink: 0, background: inactive ? '#fef2f2' : '#f0fdf4', color: inactive ? '#dc2626' : '#15803d', border: `1px solid ${inactive ? '#fecdd3' : '#bbf7d0'}` }}>
                      <span style={{ width: 5, height: 5, borderRadius: '50%', background: inactive ? '#dc2626' : '#16a34a', display: 'inline-block' }} />
                      {inactive ? '停止中' : '稼働中'}
                    </span>
                  </div>

                  {/* 統計行 */}
                  <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Users size={12} color="#9ca3af" />
                      <span style={{ fontSize: 18, fontWeight: 800, color: '#1d4ed8' }}>{c.user_count}</span>
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>船員</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Ship size={12} color="#9ca3af" />
                      <span style={{ fontSize: 18, fontWeight: 800, color: '#1d4ed8' }}>{c.vessel_count}</span>
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>船舶</span>
                    </div>
                  </div>

                  {/* メタ情報 */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, borderTop: '1px solid #f3f4f6', paddingTop: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Calendar size={11} color="#9ca3af" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: '#6b7280' }}>更新日：</span>
                      <RenewalBadge date={c.renewal_date} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Activity size={11} color="#9ca3af" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: '#6b7280' }}>最終利用：</span>
                      <span style={{ fontSize: 11, color: c.last_activity ? '#374151' : '#d1d5db', fontWeight: c.last_activity ? 500 : 400 }}>{fmtRelative(c.last_activity)}</span>
                    </div>
                    {c.sales_contact && (
                      <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        担当: <span style={{ color: '#374151' }}>{c.sales_contact}</span>
                      </div>
                    )}
                    {c.memo && (
                      <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic' }}>{c.memo}</div>
                    )}
                  </div>
                </div>

                {/* アクションバー */}
                <div style={{ borderTop: '1px solid #f3f4f6', background: '#fafafa', padding: '8px 10px', display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
                  <a href={`https://${c.subdomain}.${ADMIN_DOMAIN}`} target="_blank" rel="noreferrer"
                    style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '6px 4px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 11, fontWeight: 600, color: '#374151', textDecoration: 'none' }}>
                    <ExternalLink size={11} /> 管理
                  </a>
                  <a href={`https://${c.subdomain}.crew.${ADMIN_DOMAIN}`} target="_blank" rel="noreferrer"
                    style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '6px 4px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, fontSize: 11, fontWeight: 600, color: '#2563eb', textDecoration: 'none' }}>
                    <ExternalLink size={11} /> 船員
                  </a>
                  <button onClick={e => openQr(e, c)} style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '6px 4px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 11, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                    <QrCode size={11} /> QR
                  </button>
                  <button onClick={e => handleToggleStatus(e, c)} disabled={toggling === c.id} style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '6px 4px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', background: inactive ? '#f0fdf4' : '#fff7ed', color: inactive ? '#15803d' : '#c2410c' }}>
                    {toggling === c.id ? '…' : inactive ? <><PlayCircle size={11} /> 再開</> : <><PauseCircle size={11} /> 停止</>}
                  </button>
                  {isOwner && (
                    <button onClick={e => handleDelete(e, c)} disabled={deleting === c.id} style={{ width: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '6px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12, cursor: 'pointer', color: '#9ca3af' }}
                      onMouseEnter={e => { const b = e.currentTarget; b.style.background = '#fef2f2'; b.style.color = '#dc2626'; b.style.borderColor = '#fecdd3'; }}
                      onMouseLeave={e => { const b = e.currentTarget; b.style.background = '#fff'; b.style.color = '#9ca3af'; b.style.borderColor = '#e5e7eb'; }}>
                      {deleting === c.id ? '…' : <Trash2 size={11} />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── テーブルビュー ── */}
      {!loading && viewMode === 'table' && filtered.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {[
                  { label: '会社名', key: 'name' as SortKey },
                  { label: '状態', key: null },
                  { label: '船員/船舶', key: 'user_count' as SortKey },
                  { label: '更新日', key: 'renewal_date' as SortKey },
                  { label: '最終利用', key: 'last_activity' as SortKey },
                  { label: '担当', key: null },
                  { label: 'アクション', key: null },
                ].map(col => (
                  <th key={col.label} onClick={() => col.key && toggleSort(col.key)} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', whiteSpace: 'nowrap', cursor: col.key ? 'pointer' : 'default', userSelect: 'none' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      {col.label}
                      {col.key && <SortIcon k={col.key} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, idx) => {
                const inactive = !c.is_active;
                const renewDays = daysUntil(c.renewal_date);
                const isRenewWarn = renewDays !== null && renewDays <= 30;
                return (
                  <tr key={c.id} onClick={() => navigate(`/companies/${c.id}`)}
                    style={{ borderBottom: idx < filtered.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', transition: 'background 0.1s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontWeight: 600, color: '#111827' }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{c.subdomain}.unison-sea.com</div>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: inactive ? '#fef2f2' : '#f0fdf4', color: inactive ? '#dc2626' : '#15803d' }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: inactive ? '#dc2626' : '#16a34a' }} />
                        {inactive ? '停止中' : '稼働中'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontWeight: 700, color: '#1d4ed8' }}>{c.user_count}</span>
                      <span style={{ color: '#9ca3af', fontSize: 11 }}> 名 / </span>
                      <span style={{ fontWeight: 700, color: '#0891b2' }}>{c.vessel_count}</span>
                      <span style={{ color: '#9ca3af', fontSize: 11 }}> 隻</span>
                    </td>
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                      <RenewalBadge date={c.renewal_date} />
                      {isRenewWarn && renewDays !== null && (
                        <AlertTriangle size={12} color="#d97706" style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                      )}
                    </td>
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap', fontSize: 12, color: c.last_activity ? '#374151' : '#d1d5db' }}>
                      {fmtRelative(c.last_activity)}
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: 12, color: '#6b7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.sales_contact ?? '—'}
                    </td>
                    <td style={{ padding: '11px 14px' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <a href={`https://${c.subdomain}.${ADMIN_DOMAIN}`} target="_blank" rel="noreferrer"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 8px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#374151', textDecoration: 'none' }}>
                          <ExternalLink size={10} /> 管理
                        </a>
                        <button onClick={e => openQr(e, c)} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 8px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                          <QrCode size={10} /> QR
                        </button>
                        <button onClick={e => handleToggleStatus(e, c)} disabled={toggling === c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', background: inactive ? '#f0fdf4' : '#fff7ed', color: inactive ? '#15803d' : '#c2410c' }}>
                          {toggling === c.id ? '…' : inactive ? '再開' : '停止'}
                        </button>
                        {isOwner && (
                          <button onClick={e => handleDelete(e, c)} disabled={deleting === c.id} style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 8px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 11, cursor: 'pointer', color: '#9ca3af' }}
                            onMouseEnter={e => { const b = e.currentTarget; b.style.background = '#fef2f2'; b.style.color = '#dc2626'; b.style.borderColor = '#fecdd3'; }}
                            onMouseLeave={e => { const b = e.currentTarget; b.style.background = '#fff'; b.style.color = '#9ca3af'; b.style.borderColor = '#e5e7eb'; }}>
                            {deleting === c.id ? '…' : <Trash2 size={11} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* QRモーダル */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }} onClick={() => setModal(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 720, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{modal.company.name}</div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{modal.company.subdomain}.{ADMIN_DOMAIN}</div>
              </div>
              <button onClick={() => setModal(null)} style={{ width: 30, height: 30, background: '#f3f4f6', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 14, color: '#6b7280' }}>✕</button>
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {[
                { label: '船内の共有端末（Android・APK）', qr: modal.apkQr, href: `${DL_BASE}/?apk=catms-${modal.company.subdomain}.apk`, linkLabel: 'URLを開く', warn: '※ 船員の個人スマホには使わない' },
                { label: '船員の個人スマホ（iPhone・Android）', qr: modal.personalQr, href: `https://${modal.company.subdomain}.crew.${ADMIN_DOMAIN}/?app=personal`, linkLabel: 'URLを開く', note: '開いたら「ホーム画面に追加」' },
                { label: '管理画面ログイン', qr: modal.adminQr, href: `https://${modal.company.subdomain}.${ADMIN_DOMAIN}`, linkLabel: '管理画面を開く' },
              ].map(item => (
                <div key={item.label} style={{ flex: 1, minWidth: 190, textAlign: 'center', background: '#f9fafb', borderRadius: 12, padding: '16px 12px', border: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 12 }}>{item.label}</div>
                  <img src={item.qr} alt="QR" style={{ width: 150, height: 150, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  {'warn' in item && item.warn && <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 6 }}>{item.warn}</div>}
                  {'note' in item && item.note && <div style={{ fontSize: 11, color: '#2563eb', marginTop: 6 }}>{item.note}</div>}
                  <a href={item.href} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, fontSize: 12, color: '#1d4ed8', textDecoration: 'none', fontWeight: 500 }}>
                    <ExternalLink size={11} /> {item.linkLabel}
                  </a>
                </div>
              ))}
            </div>
            <button onClick={() => setModal(null)} style={{ width: '100%', marginTop: 20, padding: '10px', background: '#f3f4f6', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>閉じる</button>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
